/* =========================================================================
 * IBC 2.0 — QUICK VIEW
 * -------------------------------------------------------------------------
 * The expand control on a product tile ([data-ibc-quick-view]) opens one
 * panel, whose body is `sections/ibc-quickview.liquid` fetched from the
 * PRODUCT URL through the Section Rendering API:
 *
 *     <product url>?section_id=ibc-quickview&variant=<id>
 *
 * so the markup is the product page's own Liquid, with the variant the tile
 * was showing already selected. There is no second variant, add-on,
 * personalisation or add-to-cart implementation in this file — it fetches
 * markup, puts it on the screen, and gets out of the way. `<ibc-product>`
 * and `<ibc-media-gallery>` upgrade themselves the moment the fragment is
 * connected, and assets/ibc-product.js + assets/ibc-addons.js drive it from
 * there, which is what makes the line-item properties identical to the
 * product page's (contracts §4).
 *
 * Focus trap, scroll lock, Escape, the scrim click and — importantly —
 * returning focus to the control that opened the panel all come from
 * `IbcDialog` (assets/ibc-dialog.js), which this file uses and does not
 * touch. `show(button)` is what makes the return-focus contract explicit,
 * and the scroll lock is `body.no-scroll { overflow: hidden }`, not a
 * `position: fixed` body — which is why the collection page is still at the
 * same scroll offset when the panel closes.
 *
 * The panel itself is one column at every breakpoint and is sized entirely
 * in CSS (assets/ibc-quickview.css); nothing here measures the viewport or
 * writes an inline height.
 *
 * THREE ADJUSTMENTS TO THE FETCHED FRAGMENT, and why:
 *
 *   1. `<script src>` tags are stripped. A node inserted with `importNode`
 *      never runs its scripts, so leaving them in the DOM would suggest a
 *      dependency was delivered when it was not. Every script the panel
 *      needs is already on the page (see the section's own comment).
 *
 *   2. The gallery's nested zoom `<ibc-dialog>` is removed and its zoom
 *      buttons become plain `<div>`s with the same classes and the same
 *      `<img>`. Two nested modals would fight over the focus trap, and over
 *      Escape: both register a capture-phase keydown listener on `document`,
 *      and the panel — registered first — would swallow the key and close
 *      itself while the zoom stayed on screen.
 *
 *   3. The panel's `<ibc-product>` gets an own-property `_syncUrl` stub.
 *      Without it, choosing an option inside the panel would `replaceState`
 *      the product's `?variant=` onto the collection page's address bar,
 *      which is state that assets/ibc-filters.js owns. The element's FIRST
 *      selection is already made with `replaceUrl: false`, so stubbing right
 *      after insertion is early enough. The shared file is not modified;
 *      assets/ibc-addons.js does exactly the same thing for the quick-add
 *      dialog.
 * ====================================================================== */
(function () {
  'use strict';

  if (window.IbcQuickView) return;

  var SECTION_ID = 'ibc-quickview';
  var DIALOG_ID = 'IbcQuickViewDialog';
  var CLOSE_AFTER_ADD_MS = 900;
  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled])';

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  /* A control that is not rendered cannot take focus.
   *
   * This is the same offsetWidth / offsetHeight / getClientRects test
   * `assets/ibc-dialog.js` uses to build its focus trap, deliberately: the
   * two files must agree about what "focusable" means, or the panel would
   * open on a control the trap does not believe is there. */
  function visible(el) {
    return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
  }

  /* Move focus WITHOUT letting the browser scroll the control into view.
   *
   * `.ibc-qv__scroll` is the panel's own scrolling column and the first
   * purchase control sits below the image band, so a plain `focus()` scrolled
   * the panel ~320px down and the customer opened the quick view already past
   * the photograph, the title and the price. `preventScroll` keeps the focus
   * (and therefore the keyboard entry point) exactly where it was, and just
   * stops the scroll.
   *
   * A browser that does not implement the options bag ignores the argument
   * and still focuses; the try/catch is for the older engines that throw on
   * being handed one. */
  function focusNoScroll(el) {
    if (!el || typeof el.focus !== 'function') return;
    try {
      el.focus({ preventScroll: true });
    } catch (ignore) {
      el.focus();
    }
  }

  /* Always open at the top of the panel's content. Belt to focusNoScroll's
   * braces: a browser that silently ignores `preventScroll` would still land
   * the customer mid-description, so the offset is reset explicitly once the
   * fragment is on the screen. */
  function resetScroll(panel) {
    if (!panel) return;
    toArray(panel.querySelectorAll('.ibc-qv__scroll')).forEach(function (column) {
      column.scrollTop = 0;
    });
    if (typeof panel.scrollTop === 'number') panel.scrollTop = 0;
  }

  /* -------------------------------------------------------------- shell -- */

  function shell() {
    var existing = document.getElementById(DIALOG_ID);
    if (existing) return existing;

    var dialog = document.createElement('ibc-dialog');
    dialog.id = DIALOG_ID;
    dialog.className = 'dialog ibc-qv';
    dialog.hidden = true;
    dialog.innerHTML =
      '<button class="dialog__scrim" type="button" data-dialog-close tabindex="-1" aria-hidden="true"></button>' +
      '<div class="dialog__panel ibc-qv__panel" role="dialog" aria-modal="true" aria-label="Quick view" tabindex="-1">' +
      '<button type="button" class="ibc-qv__close" data-dialog-close>' +
      '<span class="visually-hidden">Close quick view</span>' +
      '<svg class="ibc-icon ibc-icon--close" width="20" height="20" viewBox="0 0 24 24" fill="none" ' +
      'aria-hidden="true" focusable="false">' +
      '<path d="m5.5 5.5 13 13m0-13-13 13" stroke="currentColor" stroke-width="1.4" ' +
      'stroke-linecap="round"></path></svg>' +
      '</button>' +
      '<div class="ibc-qv__content" data-ibc-qv-content>' +
      '<p class="ibc-qv__loading" role="status">Loading&hellip;</p>' +
      '</div>' +
      '</div>';

    document.body.appendChild(dialog);
    return dialog;
  }

  /* ------------------------------------------------------------ fragment -- */

  /* See note 2 in the header. */
  function deZoom(fragment) {
    toArray(fragment.querySelectorAll('ibc-media-gallery')).forEach(function (gallery) {
      gallery.removeAttribute('data-modal-id');
      /* The zoom dialog is emitted as a child of the gallery element, so it
       * is found by structure — no id escaping, no document lookup. */
      toArray(gallery.querySelectorAll('ibc-dialog')).forEach(function (modal) {
        if (modal.parentNode) modal.parentNode.removeChild(modal);
      });
      toArray(gallery.querySelectorAll('[data-gallery-zoom]')).forEach(function (button) {
        var plain = document.createElement('div');
        plain.className = button.className;
        while (button.firstChild) plain.appendChild(button.firstChild);
        button.parentNode.replaceChild(plain, button);
      });
    });
  }

  function scrub(fragment) {
    toArray(fragment.querySelectorAll('script[src]')).forEach(function (script) {
      script.parentNode.removeChild(script);
    });
    deZoom(fragment);
  }

  function fill(dialog, html, url) {
    var slot = dialog.querySelector('[data-ibc-qv-content]');
    if (!slot) return;

    var parsed = new DOMParser().parseFromString(html, 'text/html');
    var body =
      parsed.querySelector('[data-ibc-qv-root]') ||
      parsed.querySelector('#shopify-section-' + SECTION_ID);

    if (!body) {
      fail(dialog, url);
      return;
    }

    var fragment = document.importNode(body, true);
    scrub(fragment);

    slot.innerHTML = '';
    /* Connecting the fragment upgrades <ibc-product> and
     * <ibc-media-gallery> synchronously; their connectedCallback runs here. */
    slot.appendChild(fragment);

    var engine = slot.querySelector('ibc-product');
    if (engine) engine._syncUrl = function () {};

    var panel = dialog.querySelector('.dialog__panel');
    var title = slot.querySelector('[data-ibc-qv-title]');
    if (panel && title) {
      if (!title.id) title.id = 'ibc-qv-title';
      panel.setAttribute('aria-labelledby', title.id);
      panel.removeAttribute('aria-label');
    }

    /* assets/ibc-addons.js initialises on `ibc:dialog:open`. The panel
     * dispatched that when it opened, before this content existed, so it is
     * re-announced now that the add-on markup is really here. The element's
     * own `ibc:product-selection` (fired during the upgrade above) already
     * covers the common case; this is the belt. */
    dialog.dispatchEvent(new CustomEvent('ibc:dialog:open', { bubbles: true }));

    /* Move focus onto the first real PURCHASE control, so a keyboard
     * customer starts inside the form.
     *
     * The regions are named one by one rather than swept with a single
     * `.ibc-qv__scroll button` selector, because the image band now sits
     * inside the scrolling column and the gallery's own prev/next arrows
     * come first in document order — landing on "Previous image" is not
     * where anybody wants to start. If a product has no options and no
     * add-ons, focus falls through to Add to basket in the foot. */
    focusFirst(panel, ['.ibc-qv__options', '.ibc-qv__addons', '.ibc-qv__row', '.ibc-qv__foot']);

    /* Last, so it wins over anything the focus above may still have moved. */
    resetScroll(panel);
  }

  /* Walk the regions in order and start the customer on the first control
   * that can ACTUALLY take focus.
   *
   * Two bugs lived here, and they compounded:
   *
   *   1. `querySelector(FOCUSABLE)` returned the region's first MATCHING
   *      control, not its first USABLE one. `.ibc-qv__options` leads with a
   *      `display: none` radio per variant — the visible controls are the
   *      labels beside them — so on every product with options the pick was
   *      an unrendered input and `focus()` silently did nothing.
   *   2. Having picked it, the loop `return`ed. `.ibc-qv__addons`,
   *      `.ibc-qv__row` and `.ibc-qv__foot` — all of them holding real,
   *      visible controls — were never tried.
   *
   * So the stated intent, "a keyboard customer starts inside the form", was
   * met for no product that had options at all.
   *
   * Now each region is filtered to the controls that are really on the
   * screen, and a region that yields none is SKIPPED rather than ending the
   * walk. The move is then confirmed against `document.activeElement`: if
   * the browser refused it for a reason this predicate cannot see
   * (`visibility: hidden`, an `inert` ancestor), the walk simply carries on
   * to the next region instead of leaving focus somewhere nobody chose.
   *
   * If no region yields anything, focus is left exactly where
   * `IbcDialog.show()` put it — the close button — which is a correct and
   * conventional place for a dialog to open. Nothing is forced. */
  function focusFirst(panel, regions) {
    if (!panel) return;
    for (var i = 0; i < regions.length; i++) {
      var region = panel.querySelector(regions[i]);
      if (!region) continue;
      var control = toArray(region.querySelectorAll(FOCUSABLE)).filter(visible)[0];
      if (!control || typeof control.focus !== 'function') continue;
      focusNoScroll(control);
      if (document.activeElement === control) return;
    }
  }

  function fail(dialog, url) {
    var slot = dialog.querySelector('[data-ibc-qv-content]');
    if (!slot) return;
    slot.innerHTML =
      '<p class="ibc-qv__failed" role="status">Sorry, the quick view is unavailable right now. ' +
      '<a class="tlink" href="' +
      String(url).replace(/"/g, '&quot;') +
      '">Open the product page</a>.</p>';
  }

  /* --------------------------------------------------------------- open -- */

  function open(button) {
    var url = button.getAttribute('data-ibc-product-url');
    if (!url) return;

    var dialog = shell();
    var slot = dialog.querySelector('[data-ibc-qv-content]');
    if (slot) slot.innerHTML = '<p class="ibc-qv__loading" role="status">Loading&hellip;</p>';

    var panel = dialog.querySelector('.dialog__panel');
    if (panel) {
      panel.removeAttribute('aria-labelledby');
      panel.setAttribute(
        'aria-label',
        'Quick view — ' + (button.getAttribute('data-ibc-product-title') || 'product')
      );
    }

    /* IbcDialog returns focus to whatever is handed to `show()`, which is
     * how the expand control gets it back on close. */
    dialog.show(button);

    var separator = url.indexOf('?') === -1 ? '?' : '&';
    var request = url + separator + 'section_id=' + SECTION_ID;
    var variantId = button.getAttribute('data-ibc-variant-id');
    if (variantId) request += '&variant=' + encodeURIComponent(variantId);

    fetch(request, { headers: { Accept: 'text/html' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Section request failed');
        return response.text();
      })
      .then(function (html) {
        if (!dialog.open) return;
        fill(dialog, html, url);
      })
      .catch(function () {
        fail(dialog, url);
      });
  }

  /* ------------------------------------------------------------- wiring -- */

  document.addEventListener('click', function (event) {
    if (!event.target || !event.target.closest) return;
    var button = event.target.closest('[data-ibc-quick-view]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    open(button);
  });

  /* The panel closes itself once the add has landed, so the customer is
   * returned to the grid with the basket already updated. The status region
   * has had its beat to announce "Added to your basket." by then. */
  document.addEventListener('ibc:cart-changed', function () {
    var dialog = document.getElementById(DIALOG_ID);
    if (!dialog || !dialog.open) return;
    window.setTimeout(function () {
      if (dialog.open) dialog.hide();
    }, reducedMotion() ? 200 : CLOSE_AFTER_ADD_MS);
  });

  /* Empty the panel once it is off the screen: that disconnects
   * <ibc-product>, which deregisters itself and its listeners, so the next
   * open starts from a clean fragment rather than a stale one. */
  document.addEventListener('ibc:dialog:close', function (event) {
    var dialog = event.target;
    if (!dialog || dialog.id !== DIALOG_ID) return;
    window.setTimeout(
      function () {
        if (dialog.open) return;
        var slot = dialog.querySelector('[data-ibc-qv-content]');
        if (slot) slot.innerHTML = '';
      },
      reducedMotion() ? 0 : 420
    );
  });

  window.IbcQuickView = {
    open: open,
    close: function () {
      var dialog = document.getElementById(DIALOG_ID);
      if (dialog && dialog.open) dialog.hide();
    }
  };
})();
